/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { createBrowserHistory } from "history";
import NotFound from "./components/NotFound";
import Setting from "./container/Setting/index";
import Home from "./container/Home";
import Launch from "./container/Launch";
import Footer from "./components/Footer/index";
import Header from "./components/Header/index";
import { useAppStateProvider } from "./contextProviders/AppStateProvider";
import { InfoProvider, AppLaunchProvider } from "./contextProviders/index";
import PageError from "./components/PageError";
import getCookie from "./util/cookie";
import { extractLanguageFromPathname } from "./util/helper";
import "./styles/App.css";

const history = createBrowserHistory({ forceRefresh: true });

const App = () => {
  const [lang, setLang] = useState(
    window.localStorage?.getItem("lang") ?? "en"
  );
  const { appState = {}, updateAppStateLocal } = useAppStateProvider();
  const {
    isError = false,
    preferred_language = "en",
    isPopUpBlocked,
    isLoading,
  } = appState;

  const handleLang = (historyObj) => {
    const { action = "", location = {} } = historyObj;
    const { pathname = "", hash = "" } = location;
    if (
      action?.toString().toLowerCase() === "push" &&
      !extractLanguageFromPathname(pathname) &&
      !hash
    ) {
      history.push(
        `/${lang}${
          (window?.location?.pathname ?? "") +
          (window?.location?.search ?? "") +
          (window?.location?.hash ?? "")
        }`
      );
      window.location.reload();
    } else if (action?.toString().toLowerCase() === "push")
      window.location.reload();
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window?.location?.search) ?? "";
    const pathLang =
      extractLanguageFromPathname(window.location.pathname) ?? null;
    const cookieLang = getCookie("lang");
    const language =
      pathLang ??
      searchParams?.get("lang") ??
      cookieLang ??
      preferred_language ??
      "en";
    setLang(language.toLowerCase());
    const domain = /local/.test(window.location.host)
      ? "localhost"
      : "moneris.com";
    document.cookie = `lang=${language}; expires=Thu, 18 Dec 2032 12:00:00 UTC;domain=${domain};path=/`;
    window.localStorage.setItem("lang", language.toLowerCase());
    (document.getElementsByTagName('html')[0]).setAttribute('lang',language);
  }, [preferred_language]);

  useEffect(() => {
    history.listen((historyObj) => handleLang(historyObj));
    if (
      !extractLanguageFromPathname(window?.location?.pathname) &&
      !window.location.hash
    ) {
      history.push(
        `/${lang}${
          (window?.location?.pathname ?? "/home") +
          (window?.location?.search ?? "") +
          (window?.location?.hash ?? "")
        }`
      );
      window.location.reload();
    }
    if (
      (window?.location?.pathname ?? "")?.toLowerCase() === "/en/" ||
      (window?.location?.pathname ?? "")?.toLowerCase() === "/fr/" ||
      (window?.location?.pathname ?? "")?.toLowerCase() === "/en" ||
      (window?.location?.pathname ?? "")?.toLowerCase() === "/fr"
    ) {
      history.push(
        `/${lang}/home${
          (window?.location?.search ?? "") + (window?.location?.hash ?? "")
        }`
      );
      window.location.reload();
    }
  }, []);

  return (
    <Router history={history}>
      <div id="main-content">
        <InfoProvider>
          <Header lang={lang} history={history} />
        </InfoProvider>
        {isError ? (
          <Route path={`/${lang}/*`}>
            <PageError lang={lang} history={history} />
          </Route>
        ) : (
          <Switch>
            <Route path={`/${lang}/login`}>
              <InfoProvider>
                <AppLaunchProvider>
                  <Home lang={lang} history={history} />
                </AppLaunchProvider>
              </InfoProvider>
            </Route>
            <Route path={`/${lang}/home`}>
              <InfoProvider>
                <AppLaunchProvider>
                  <Home lang={lang} history={history} />
                </AppLaunchProvider>
              </InfoProvider>
            </Route>
            <Route path={`/${lang}/setting`}>
              <InfoProvider>
                <Setting lang={lang} history={history} />
              </InfoProvider>
            </Route>
            <Route path={`/${lang}/launch`}>
              <InfoProvider>
                <AppLaunchProvider>
                  <Launch lang={lang} history={history} />
                </AppLaunchProvider>
              </InfoProvider>
            </Route>
            <Route path={`/${lang}/*`}>
              <NotFound lang={lang} history={history} />
            </Route>
          </Switch>
        )}
      </div>
      <Footer lang={lang} history={history} />
    </Router>
  );
};

export default App;
